import React, { createContext, useContext, useState, useEffect } from 'react';
import { getApiUrl } from '../config';

const UGCContext = createContext();

export const UGCProvider = ({ children, sessionData = null }) => {
  const [ugcEnabled, setUgcEnabled] = useState(true); // Default to enabled
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(Date.now());

  const fetchUgcStatus = async () => {
    try {
      const response = await fetch(getApiUrl('/ugc-status'));

      if (response.ok) {
        const data = await response.json();
        setUgcEnabled(data.ugcEnabled);
        setLastUpdated(Date.now());
      } else {
        // Handle HTTP errors like 404 the same as network errors
        console.error('UGC status endpoint returned:', response.status);
        setUgcEnabled(true);
        setLastUpdated(Date.now());
      }
    } catch (err) {
      console.error('Failed to fetch UGC status:', err);
      // Default to enabled on error to avoid breaking functionality
      setUgcEnabled(true);
      setLastUpdated(Date.now());
    }
  };

  const updateUgcStatus = (newStatus) => {
    setUgcEnabled(newStatus);
    setLastUpdated(Date.now());
  };

  // Check UGC status on mount
  useEffect(() => {
    fetchUgcStatus();
  }, []);

  // Helper function to determine if UGC should be shown
  const shouldShowUGC = (isAdmin = false) => {
    // Admins can always see UGC regardless of setting
    return isAdmin || ugcEnabled;
  };

  // Helper function to get cache-busted image URL
  const getImageUrl = (url, isAdmin = false) => {
    if (!url) return null;
    
    // If UGC is disabled and user is not admin, don't return the URL
    if (!shouldShowUGC(isAdmin)) {
      return null;
    }
    
    // If it's a relative URL (local upload), construct the full URL with cache buster
    if (url.startsWith('/uploads/')) {
      const fullUrl = getApiUrl(url);
      const params = new URLSearchParams();
      
      // Add cache buster based on when UGC status was last updated
      params.set('t', lastUpdated.toString());
      
      // If user is admin and we have session data, add auth token for image access
      if (isAdmin && sessionData?.token) {
        params.set('token', sessionData.token);
      }
      
      return `${fullUrl}?${params.toString()}`;
    }
    
    // If it's already a full URL (external), use as-is
    return url;
  };

  const value = {
    ugcEnabled,
    loading,
    lastUpdated,
    fetchUgcStatus,
    updateUgcStatus,
    shouldShowUGC,
    getImageUrl
  };

  return (
    <UGCContext.Provider value={value}>
      {children}
    </UGCContext.Provider>
  );
};

export const useUGC = () => {
  const context = useContext(UGCContext);
  if (!context) {
    throw new Error('useUGC must be used within a UGCProvider');
  }
  return context;
}; 